import { request } from "$/lib/api"
import {
  BIKE_FOR_SALE_PUBLICATION_TARGET_LABELS,
  BikeForSaleActiveStatus,
  BikeForSaleAwaitingHandoverCountDto,
  BikeForSaleDetailDto,
  BikeForSaleInspectionDto,
  BikeForSaleInspectionPrerequisitesDto,
  BikeForSaleInspectionProtocolDto,
  BikeForSaleMoveTargetDto,
  BikeForSaleProposalDto,
  BikeForSaleProposalPreviewInput,
  BikeForSalePublicationDto,
  BikeForSaleSummary,
  CreateBikeForSale,
  MarkBikeForSaleHandedOver,
  MoveBikeForSale,
  Route,
  SubmitBikeForSaleInspection,
  SubmitBikeForSaleListing,
  UpdateBikeForSale,
  UpdateBikeForSaleInspectionProduct,
  populateRoute,
} from "@repo/common"
import { toast } from "@repo/ui/hooks/use-toast"
import { useTranslateApiError } from "@repo/ui/lib/translate-api-error"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useTranslations } from "use-intl"

const fetchList = async (storeId: string): Promise<BikeForSaleSummary[]> =>
  request<BikeForSaleSummary[]>({
    url: populateRoute(
      Route.BIKE_FOR_SALE_CONTROLLER__FIND_MANY_BY_STORE_ID,
      storeId,
    ),
    method: "GET",
  })

const fetchOne = async (bikeId: string): Promise<BikeForSaleDetailDto> =>
  request<BikeForSaleDetailDto>({
    url: populateRoute(Route.BIKE_FOR_SALE_CONTROLLER__FIND_ONE_BY_ID, bikeId),
    method: "GET",
  })

const fetchInspectionPrerequisites = async (
  bikeId: string,
): Promise<BikeForSaleInspectionPrerequisitesDto> =>
  request<BikeForSaleInspectionPrerequisitesDto>({
    url: populateRoute(
      Route.BIKE_FOR_SALE_CONTROLLER__GET_INSPECTION_PREREQS,
      bikeId,
    ),
    method: "GET",
  })

const fetchInspection = async (
  bikeId: string,
): Promise<BikeForSaleInspectionDto | null> =>
  request<BikeForSaleInspectionDto | null>({
    url: populateRoute(Route.BIKE_FOR_SALE_CONTROLLER__FIND_INSPECTION, bikeId),
    method: "GET",
  })

const fetchInspectionProtocol = async (
  bikeId: string,
): Promise<BikeForSaleInspectionProtocolDto | null> =>
  request<BikeForSaleInspectionProtocolDto | null>({
    url: populateRoute(
      Route.BIKE_FOR_SALE_CONTROLLER__FIND_INSPECTION_PROTOCOL,
      bikeId,
    ),
    method: "GET",
  })

const previewProposal = async (
  bikeId: string,
  input: BikeForSaleProposalPreviewInput,
): Promise<BikeForSaleProposalDto> =>
  request<BikeForSaleProposalDto>({
    url: populateRoute(
      Route.BIKE_FOR_SALE_CONTROLLER__PROPOSAL_PREVIEW,
      bikeId,
    ),
    method: "POST",
    data: input,
  })

const createBike = async (
  storeId: string,
  data: CreateBikeForSale,
): Promise<BikeForSaleSummary> =>
  request<BikeForSaleSummary>({
    url: populateRoute(Route.BIKE_FOR_SALE_CONTROLLER__CREATE, storeId),
    method: "POST",
    data,
  })

const updateBike = async (
  bikeId: string,
  data: UpdateBikeForSale,
): Promise<BikeForSaleSummary> =>
  request<BikeForSaleSummary>({
    url: populateRoute(Route.BIKE_FOR_SALE_CONTROLLER__UPDATE, bikeId),
    method: "PATCH",
    data,
  })

const updateStatus = async (
  bikeId: string,
  status: BikeForSaleActiveStatus,
): Promise<BikeForSaleSummary> =>
  request<BikeForSaleSummary>({
    url: populateRoute(Route.BIKE_FOR_SALE_CONTROLLER__UPDATE_STATUS, bikeId),
    method: "PATCH",
    data: { status },
  })

const markAsSold = async (bikeId: string): Promise<BikeForSaleSummary> =>
  request<BikeForSaleSummary>({
    url: populateRoute(Route.BIKE_FOR_SALE_CONTROLLER__MARK_AS_SOLD, bikeId),
    method: "POST",
  })

const markAsHandedOver = async (
  bikeId: string,
  body: MarkBikeForSaleHandedOver,
): Promise<BikeForSaleSummary> =>
  request<BikeForSaleSummary>({
    url: populateRoute(
      Route.BIKE_FOR_SALE_CONTROLLER__MARK_AS_HANDED_OVER,
      bikeId,
    ),
    method: "POST",
    data: body,
  })

const fetchAwaitingHandoverCount = async (
  storeId: string,
): Promise<BikeForSaleAwaitingHandoverCountDto> =>
  request<BikeForSaleAwaitingHandoverCountDto>({
    url: populateRoute(
      Route.BIKE_FOR_SALE_CONTROLLER__AWAITING_HANDOVER_COUNT,
      storeId,
    ),
    method: "GET",
  })

const deleteBike = async (bikeId: string): Promise<void> =>
  request<void>({
    url: populateRoute(Route.BIKE_FOR_SALE_CONTROLLER__DELETE, bikeId),
    method: "DELETE",
  })

const submitInspection = async (
  bikeId: string,
  data: SubmitBikeForSaleInspection,
): Promise<BikeForSaleInspectionDto> =>
  request<BikeForSaleInspectionDto>({
    url: populateRoute(
      Route.BIKE_FOR_SALE_CONTROLLER__SUBMIT_INSPECTION,
      bikeId,
    ),
    method: "POST",
    data,
  })

const createAccountingRecord = async (
  bikeId: string,
): Promise<BikeForSaleSummary> =>
  request<BikeForSaleSummary>({
    url: populateRoute(
      Route.BIKE_FOR_SALE_CONTROLLER__CREATE_ACCOUNTING_RECORD,
      bikeId,
    ),
    method: "POST",
  })

const updateInspectionProduct = async (
  bikeId: string,
  productId: string,
  data: UpdateBikeForSaleInspectionProduct,
): Promise<void> =>
  request<void>({
    url: populateRoute(
      Route.BIKE_FOR_SALE_CONTROLLER__UPDATE_INSPECTION_PRODUCT,
      bikeId,
      productId,
    ),
    method: "PATCH",
    data,
  })

const deleteInspectionProduct = async (
  bikeId: string,
  productId: string,
): Promise<void> =>
  request<void>({
    url: populateRoute(
      Route.BIKE_FOR_SALE_CONTROLLER__DELETE_INSPECTION_PRODUCT,
      bikeId,
      productId,
    ),
    method: "DELETE",
  })

export interface SubmitListingArgs {
  bikeId: string
  payload: SubmitBikeForSaleListing
  files: ReadonlyArray<{ fileKey: string; file: File }>
}

const submitListing = async ({
  bikeId,
  payload,
  files,
}: SubmitListingArgs): Promise<void> => {
  const formData = new FormData()
  for (const { fileKey, file } of files) {
    formData.append(fileKey, file)
  }
  formData.append("payload", JSON.stringify(payload))
  return request<void>({
    url: populateRoute(Route.BIKE_FOR_SALE_CONTROLLER__SUBMIT_LISTING, bikeId),
    method: "POST",
    data: formData,
  })
}

const publishBike = async (
  bikeId: string,
): Promise<BikeForSalePublicationDto> =>
  request<BikeForSalePublicationDto>({
    url: populateRoute(Route.BIKE_FOR_SALE_CONTROLLER__PUBLISH, bikeId),
    method: "POST",
  })

const fetchMoveTargets = async (
  bikeId: string,
): Promise<BikeForSaleMoveTargetDto[]> =>
  request<BikeForSaleMoveTargetDto[]>({
    url: populateRoute(Route.BIKE_FOR_SALE_CONTROLLER__MOVE_TARGETS, bikeId),
    method: "GET",
  })

const moveToStore = async (
  bikeId: string,
  data: MoveBikeForSale,
): Promise<BikeForSaleDetailDto> =>
  request<BikeForSaleDetailDto>({
    url: populateRoute(Route.BIKE_FOR_SALE_CONTROLLER__MOVE_TO_STORE, bikeId),
    method: "POST",
    data,
  })

const setJuloPublication = async (
  bikeId: string,
  publishedOnJulo: boolean,
): Promise<BikeForSaleSummary> =>
  request<BikeForSaleSummary>({
    url: populateRoute(
      Route.BIKE_FOR_SALE_CONTROLLER__SET_JULO_PUBLICATION,
      bikeId,
    ),
    method: "PATCH",
    data: { publishedOnJulo },
  })

const bikeForSaleQueries = {
  all: () => ["bikeForSale"],
  lists: () => [...bikeForSaleQueries.all(), "list"],
  list: (storeId: string) =>
    queryOptions({
      queryKey: [...bikeForSaleQueries.lists(), storeId],
      queryFn: () => fetchList(storeId),
    }),
  details: () => [...bikeForSaleQueries.all(), "detail"],
  detail: (bikeId: string) =>
    queryOptions({
      queryKey: [...bikeForSaleQueries.details(), bikeId],
      queryFn: () => fetchOne(bikeId),
    }),
  inspectionPrereqs: (bikeId: string) =>
    queryOptions({
      queryKey: [...bikeForSaleQueries.all(), "prereqs", bikeId],
      queryFn: () => fetchInspectionPrerequisites(bikeId),
    }),
  inspection: (bikeId: string) =>
    queryOptions({
      queryKey: [...bikeForSaleQueries.all(), "inspection", bikeId],
      queryFn: () => fetchInspection(bikeId),
    }),
  inspectionProtocol: (bikeId: string) =>
    queryOptions({
      queryKey: [...bikeForSaleQueries.all(), "inspectionProtocol", bikeId],
      queryFn: () => fetchInspectionProtocol(bikeId),
    }),
  moveTargets: (bikeId: string) =>
    queryOptions({
      queryKey: [...bikeForSaleQueries.all(), "moveTargets", bikeId],
      queryFn: () => fetchMoveTargets(bikeId),
    }),
  // Lives under the bikeForSale key prefix on purpose — the mutations'
  // invalidate() then refreshes the sidebar badge and handover banner too.
  awaitingHandoverCount: (storeId: string) =>
    queryOptions({
      queryKey: [...bikeForSaleQueries.all(), "awaitingHandoverCount", storeId],
      queryFn: () => fetchAwaitingHandoverCount(storeId),
      staleTime: 5000,
    }),
}

export function useBikeForSaleMutations(storeId: string) {
  const queryClient = useQueryClient()
  const translateApiError = useTranslateApiError()
  const t = useTranslations("alfred.lib.bikesForSale")

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: bikeForSaleQueries.all(),
    })
  }

  // Product edits only affect a single bike — invalidate its queries instead
  // of refetching every bike-for-sale query in the cache.
  const invalidateBike = async (bikeId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: bikeForSaleQueries.detail(bikeId).queryKey,
      }),
      queryClient.invalidateQueries({
        queryKey: bikeForSaleQueries.inspection(bikeId).queryKey,
      }),
      queryClient.invalidateQueries({
        queryKey: bikeForSaleQueries.inspectionProtocol(bikeId).queryKey,
      }),
      queryClient.invalidateQueries({
        queryKey: bikeForSaleQueries.inspectionPrereqs(bikeId).queryKey,
      }),
    ])
  }

  const createMutation = useMutation({
    mutationFn: (data: CreateBikeForSale) => createBike(storeId, data),
    onSettled: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({
      bikeId,
      data,
    }: { bikeId: string; data: UpdateBikeForSale }) => updateBike(bikeId, data),
    onSettled: invalidate,
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({
      bikeId,
      status,
    }: { bikeId: string; status: BikeForSaleActiveStatus }) =>
      updateStatus(bikeId, status),
    onSettled: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: (bikeId: string) => deleteBike(bikeId),
    onSettled: invalidate,
  })

  const submitInspectionMutation = useMutation({
    mutationFn: ({
      bikeId,
      data,
    }: {
      bikeId: string
      data: SubmitBikeForSaleInspection
    }) => submitInspection(bikeId, data),
    onSettled: invalidate,
  })

  const previewProposalMutation = useMutation({
    mutationFn: ({
      bikeId,
      input,
    }: {
      bikeId: string
      input: BikeForSaleProposalPreviewInput
    }) => previewProposal(bikeId, input),
  })

  const createAccountingRecordMutation = useMutation({
    mutationFn: (bikeId: string) => createAccountingRecord(bikeId),
    onError: (error) => {
      toast({
        title: t("accountingRecordFailed"),
        description: translateApiError(error),
        variant: "destructive",
      })
    },
    onSettled: invalidate,
  })

  const updateInspectionProductMutation = useMutation({
    mutationFn: async ({
      bikeId,
      productId,
      data,
    }: {
      bikeId: string
      productId: string
      data: UpdateBikeForSaleInspectionProduct
    }) => {
      try {
        await updateInspectionProduct(bikeId, productId, data)
      } finally {
        // Invalidate in a finally instead of onSettled so we have access to
        // the per-call bikeId without unused callback parameters.
        await invalidateBike(bikeId)
      }
    },
  })

  const deleteInspectionProductMutation = useMutation({
    mutationFn: async ({
      bikeId,
      productId,
    }: { bikeId: string; productId: string }) => {
      try {
        await deleteInspectionProduct(bikeId, productId)
      } finally {
        await invalidateBike(bikeId)
      }
    },
  })

  const submitListingMutation = useMutation({
    mutationFn: submitListing,
    onSettled: invalidate,
  })

  const publishMutation = useMutation({
    mutationFn: (bikeId: string) => publishBike(bikeId),
    onError: (error) => {
      toast({
        title: t("publishFailed"),
        description: translateApiError(error, t("publishFailedFallback")),
        variant: "destructive",
      })
    },
    onSuccess: (publication) => {
      const label = BIKE_FOR_SALE_PUBLICATION_TARGET_LABELS[publication.target]
      toast({
        title: t("published.title"),
        description: t("published.description", { target: label }),
      })
    },
    onSettled: invalidate,
  })

  const markAsSoldMutation = useMutation({
    mutationFn: (bikeId: string) => markAsSold(bikeId),
    onError: (error) => {
      toast({
        title: t("markSoldFailed"),
        description: translateApiError(error),
        variant: "destructive",
      })
    },
    onSuccess: () => {
      toast({
        title: t("markedSold.title"),
        description: t("markedSold.description"),
      })
    },
    onSettled: invalidate,
  })

  const markAsHandedOverMutation = useMutation({
    mutationFn: ({
      bikeId,
      ...body
    }: { bikeId: string } & MarkBikeForSaleHandedOver) =>
      markAsHandedOver(bikeId, body),
    onError: (error) => {
      toast({
        title: t("markHandedOverFailed"),
        description: translateApiError(error),
        variant: "destructive",
      })
    },
    onSuccess: () => {
      toast({
        title: t("markedHandedOver.title"),
        description: t("markedHandedOver.description"),
      })
    },
    onSettled: invalidate,
  })

  const moveMutation = useMutation({
    mutationFn: ({ bikeId, data }: { bikeId: string; data: MoveBikeForSale }) =>
      moveToStore(bikeId, data),
    onError: (error) => {
      toast({
        title: t("moveFailed"),
        description: translateApiError(error),
        variant: "destructive",
      })
    },
    onSettled: invalidate,
  })

  const setJuloPublicationMutation = useMutation({
    mutationFn: ({
      bikeId,
      publishedOnJulo,
    }: { bikeId: string; publishedOnJulo: boolean }) =>
      setJuloPublication(bikeId, publishedOnJulo),
    onError: (error) => {
      toast({
        title: t("juloPublicationFailed"),
        description: translateApiError(error),
        variant: "destructive",
      })
    },
    onSettled: invalidate,
  })

  return {
    createMutation,
    updateMutation,
    updateStatusMutation,
    deleteMutation,
    submitInspectionMutation,
    previewProposalMutation,
    createAccountingRecordMutation,
    updateInspectionProductMutation,
    deleteInspectionProductMutation,
    submitListingMutation,
    publishMutation,
    setJuloPublicationMutation,
    markAsSoldMutation,
    markAsHandedOverMutation,
    moveMutation,
  }
}

export { previewProposal }
export default bikeForSaleQueries
